import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

const BUDGET_OPTIONS = [
  { value: 'cash',       label: 'Cash on hand' },
  { value: 'financing',  label: 'Financing pre-approved' },
  { value: 'exploring',  label: 'Exploring financing' },
];

const TIMELINE_OPTIONS = [
  { value: 'matches', label: 'Matches listing' },
  { value: 'faster',  label: 'Faster' },
  { value: 'slower',  label: 'Slower' },
];

export default function ExpressInterestModal({
  isOpen,
  onClose,
  onSubmit,           // ({ interestMessage, budgetReadiness, timelineAlignment }) => Promise
  buyerProfile = null, // { summary: 'Strategic acquirer, $1M-$5M target, GTA' } or null
  submitting = false,
  serverErrors = [],
}) {
  const [interest,         setInterest]         = useState('');
  const [budgetReadiness,  setBudgetReadiness]  = useState('cash');
  const [timelineAlignment, setTimelineAlignment] = useState('matches');
  const [submitted,        setSubmitted]        = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const result = await onSubmit?.({
      interestMessage:    interest,
      budgetReadiness,
      timelineAlignment,
    });
    if (result?.ok) {
      setSubmitted(true);
      // Auto-close after the success state has a moment to render.
      setTimeout(() => {
        onClose?.();
        setSubmitted(false);
      }, 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="express-interest-title"
        className="relative bg-card border border-border rounded-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        {submitted ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="font-serif text-[22px] font-light text-foreground mb-2">
              Interest submitted
            </h2>
            <p className="text-[13px] text-muted-foreground">
              A broker will be in touch within 24 hours.
            </p>
          </div>
        ) : (
          <>
            <div className="border-b border-border p-6">
              <h2 id="express-interest-title" className="font-serif text-[22px] font-light text-foreground mb-2">
                Express interest in this listing
              </h2>
              <p className="text-[13px] text-muted-foreground">
                Your name and contact details are only shared if the seller approves an NDA.
              </p>
            </div>

            <div className="p-6 space-y-6">
              {serverErrors.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-[13px] text-destructive">
                  {serverErrors.join('\n')}
                </div>
              )}

              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <div className="text-[13px] text-foreground mb-2">
                  <strong>You</strong> — {buyerProfile?.summary || 'Buyer profile not yet completed'}
                </div>
                <a
                  href="/succeedo/buyer-readiness"
                  className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline"
                >
                  {buyerProfile ? 'Update buyer profile →' : 'Complete buyer profile →'}
                </a>
              </div>

              <Field label="Why are you interested?">
                <textarea
                  value={interest}
                  onChange={(e) => setInterest(e.target.value.slice(0, 200))}
                  maxLength={200}
                  rows={3}
                  placeholder="Share what makes this opportunity a good fit..."
                  className="w-full bg-background border border-input rounded-lg px-4 py-3 text-foreground text-[14px] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none resize-none"
                />
                <div className="text-[11px] text-muted-foreground text-right mt-1">
                  {interest.length}/200
                </div>
              </Field>

              <Field label="Budget readiness">
                <div className="space-y-2">
                  {BUDGET_OPTIONS.map((option) => (
                    <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="budget"
                        value={option.value}
                        checked={budgetReadiness === option.value}
                        onChange={(e) => setBudgetReadiness(e.target.value)}
                        className="w-4 h-4 text-[hsl(var(--succeedo-orange))] focus:ring-[hsl(var(--succeedo-orange))] focus:ring-offset-0"
                      />
                      <span className="text-[13px] text-foreground">{option.label}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Timeline alignment">
                <div className="flex flex-wrap gap-2">
                  {TIMELINE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTimelineAlignment(option.value)}
                      className={`px-4 py-2 rounded-full text-[12px] transition-colors ${
                        timelineAlignment === option.value
                          ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div className="border-t border-border p-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="border border-border text-foreground rounded-full px-5 py-[9px] text-sm hover:bg-accent transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-[9px] text-sm hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit Interest'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase text-muted-foreground mb-2 tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}
